// @flow

import * as React from 'react';
import classnames from '@ta/common.classnames';
import GlobalNavApiBinding from '@ta/brand.global-nav';
import type { NavLinkType } from '@ta/brand.global-nav';
import type { GeoPillSponsorDataProvider } from '@ta/brand.sponsored-geopill';
import { Device } from '@ta/lithium.context';

import QuickLinksLiteItem from './QuickLinksLiteItem';
import type { QuickLinksMoreMenuData } from './types';
import styles from './QuickLinksLite.less';

type Props = {|
  /** Links to show */
  links: ?(NavLinkType[]),

  /** ID of the NavLinkType[] that should show as active */
  activeLinkId: ?string,

  /** Whether the page we are on is scoped to a geo. */
  isGeoScoped: boolean,

  /** Whether we should show 5 or 6 quick links plus more link.. */
  extendedQuickLinks: boolean,

  /** Whether to show just text or just icons. */
  displayMode?: 'TEXT' | 'ICONS',

  /** Which color scheme to use */
  color?: 'green' | 'black',

  /** Promise for future geopill sponsor result or null if not enabled */
  sponsorDataProvider?: ?GeoPillSponsorDataProvider,

  /** Invoked when the geo pill overlay is opened */
  onGeoPillOpen?: () => void,

  /** Invoked when the geo pill overlay is closed */
  onGeoPillClose?: () => void,
|};

const MAX_SHOWN_LINKS_TABLET = 6;

/** The version of quick links that show when the header is fixed upon scrolling. */
const QuickLinksLite = ({
  links,
  activeLinkId,
  isGeoScoped,
  extendedQuickLinks,
  displayMode,
  color = 'green',
  sponsorDataProvider,
  onGeoPillOpen,
  onGeoPillClose,
}: Props) => {
  const { viewportCategory } = React.useContext(Device);

  if (!links) {
    return null;
  }

  const showLinkOnTablet = (link: NavLinkType, navLinkPosition: number) =>
    !link.forceCollapse && navLinkPosition < MAX_SHOWN_LINKS_TABLET;

  const visibleLinks = links.filter((link, idx) =>
    viewportCategory === 'TABLET' ? showLinkOnTablet(link, idx) : !link.forceCollapse,
  );
  const collapsedLinks = links.filter((link, idx) =>
    viewportCategory === 'TABLET' ? !showLinkOnTablet(link, idx) : link.forceCollapse,
  );
  const collapsedData: QuickLinksMoreMenuData = {
    links: collapsedLinks,
    activeLinkId,
  };

  const classes = classnames(
    'ui_columns',
    'is-mobile',
    'is-multiline',
    'is-gapless',
    styles.quickLinks,
  );

  return (
    <div className={classes} data-test-quick-links-lite>
      {visibleLinks.map(link => (
        <QuickLinksLiteItem
          link={link}
          isActive={!!activeLinkId && activeLinkId === link.id}
          key={link.id}
          isGeoScoped={isGeoScoped}
          displayMode={displayMode === 'ICONS' ? 'ICON' : 'TEXT'}
          color={color}
          extendedQuickLinks={extendedQuickLinks}
          sponsorDataProvider={sponsorDataProvider}
          onGeoPillOpen={onGeoPillOpen}
          onGeoPillClose={onGeoPillClose}
        />
      ))}
      <QuickLinksLiteItem
        isMoreTrigger
        morePopoverFixed
        collapsedData={collapsedData}
        displayMode={displayMode === 'ICONS' ? 'ICON' : 'TEXT'}
        color={color}
        extendedQuickLinks={extendedQuickLinks}
        sponsorDataProvider={sponsorDataProvider}
      />
    </div>
  );
};

QuickLinksLite.defaultProps = {
  displayMode: 'ICONS',
};

export default GlobalNavApiBinding(QuickLinksLite);
